import { getDistance } from '@lib/google/places/browser'
import { PGA_COACH_DISTANCE_OPTIONS } from '@lib/pga-coach/constants'

export const getWidestExpandableDistanceId = (distanceId) => {
  const wider = PGA_COACH_DISTANCE_OPTIONS.filter((option) => Number(option.id) > Number(distanceId))
  if (!wider.length) return null
  return wider.reduce((widest, option) => (
    Number(option.miles) > Number(widest.miles) ? option : widest
  )).id
}

export const getCoachSearchEmptyAutoExpandLocationKey = (searchState = {}) => (
  searchState.placeName || ''
)

export const isJourneySearchFullyRelaxed = (searchState = {}, journeyDefaults = {}) => (
  !searchState.persona
  && !searchState.query
  && (searchState.formats?.length ?? 0) === 0
  && (searchState.privateCategories?.length ?? 0) === 0
  && searchState.price === journeyDefaults.price
  && searchState.schedule === journeyDefaults.schedule
  && searchState.sort === journeyDefaults.sort
  && !searchState.savedOfferingsOnly
  && searchState.distance === journeyDefaults.distance
)

export const getCoachSearchEmptyAutoExpandAction = ({
  isLoading,
  hasSearchResults,
  savedOfferingsOnly,
  isDetectingLocation,
  currentPage,
  errorMessage,
  isMapDragSearch = false,
  isSearchSettled = true,
  searchState = {},
  journeyDefaults = {},
  hasOfferingsInAreaIgnoringPersona,
  didAutoExpandDistance = false,
  didAutoShowBest = false,
} = {}) => {
  if (
    isLoading
    || !isSearchSettled
    || hasSearchResults
    || savedOfferingsOnly
    || isDetectingLocation
    || currentPage !== 1
    || errorMessage
    || isMapDragSearch
  ) return null
  if (!searchState.placeName && !searchState.aroundLatLng) return null

  const toDistance = getWidestExpandableDistanceId(searchState.distance)
  if (toDistance && !didAutoExpandDistance) {
    return {
      type: 'expand_distance',
      fromDistance: String(searchState.distance),
      toDistance,
    }
  }

  if (
    hasOfferingsInAreaIgnoringPersona
    && !didAutoShowBest
    && !isJourneySearchFullyRelaxed(searchState, journeyDefaults)
  ) {
    return { type: 'show_best' }
  }

  return null
}

export const getCoachSearchAutoExpandNoticeCopy = (notice) => {
  if (!notice) return ''
  if (notice.type === 'expand_distance') {
    const fromMiles = Number(notice.fromMiles)
    const toMiles = Number(notice.toMiles)
    return `No programs found within ${fromMiles} miles. Search was expanded to ${toMiles} miles.`
  }
  return "We couldn't find an exact match, so we broadened your search."
}

const PERSONA_PROGRESS_CONTENT = {
  'start-golf': {
    title: 'New to golf? Start with the basics.',
  },
  'play-better': {
    title: 'Looking to improve your game? Start here.',
  },
  juniors: {
    title: "Getting your junior started? Here's what to know.",
  },
  'break-100': {
    title: 'Working to lower your scores? Start here.',
  },
  'womens-golf': {
    title: 'Looking for a welcoming way to learn? Start here.',
  },
  'military-veterans': {
    title: 'Looking for veteran-friendly next steps? Start here.',
  },
}

const pickFirstDefinedString = (...values) => {
  const found = values.find((value) => value !== undefined && value !== null && value !== '')
  return found == null ? null : String(found)
}

/**
 * Facility id for analytics — sourced from Algolia raw hits (private offerings, coach tools).
 */
export const getAnalyticsFacilityIdFromOffering = (offering) => {
  if (!offering) return null
  if (offering.facilityId) return String(offering.facilityId)
  const hit = offering.rawHit || {}
  const primaryFacility = hit.facilities?.[0]
  return pickFirstDefinedString(
    primaryFacility?.facility_id,
    primaryFacility?.facilityId,
    hit.facility_id,
    hit.facilityId,
    hit.coach_facility_id,
    hit.coachFacilityId,
  )
}

export const parseLatLng = (value) => {
  if (!value || typeof value !== 'string') return null
  const [latValue, lngValue] = value.split(',').map((part) => Number(part.trim()))
  if (Number.isNaN(latValue) || Number.isNaN(lngValue)) return null
  return {
    lat: latValue,
    lng: lngValue,
  }
}

export const getOfferingCoordinates = (offering) => {
  if (!offering) return null
  const lat = Number(offering.coordinates?.lat)
  const lng = Number(offering.coordinates?.lng)
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null
  return { lat, lng }
}

export const isOfferingWithinBounds = (offering, bounds) => {
  const coordinates = getOfferingCoordinates(offering)
  if (!bounds || !coordinates) return true

  return (
    coordinates.lat <= bounds.north
    && coordinates.lat >= bounds.south
    && coordinates.lng <= bounds.east
    && coordinates.lng >= bounds.west
  )
}

export const getVisibleOfferings = (offerings, bounds) => {
  const list = offerings || []
  if (!bounds) return list
  return list.filter((offering) => isOfferingWithinBounds(offering, bounds))
}

export const getNearestOfferings = (offerings, count = 3) => (
  [...(offerings || [])]
    .sort((left, right) => (left.geoDistanceMeters || Infinity) - (right.geoDistanceMeters || Infinity))
    .slice(0, count)
)

export const isViewingOutsideSelectedArea = ({
  selectedLocation,
  mapCenter,
  radiusMiles,
}) => {
  if (!selectedLocation || !mapCenter || !radiusMiles) return false
  const distanceInKm = getDistance(selectedLocation, mapCenter)
  const distanceInMiles = distanceInKm * 0.621371
  return distanceInMiles > radiusMiles
}

export const getCoachSearchEmptyStateCopy = ({
  hasExpandableDistanceOptions,
  isCurrentViewEmpty = false,
  hasFallbackResults = false,
}) => {
  if (isCurrentViewEmpty) {
    return {
      title: 'No programs here yet',
      description: hasExpandableDistanceOptions
        ? 'Try zooming out, expanding your radius, adjusting your filters, or switching formats to see more options.'
        : "You're already searching the widest area. Try zooming out, adjusting your filters or switching formats to see more options.",
    }
  }

  if (hasFallbackResults) {
    return {
      title: "Nothing exact\u2014but we've got great options",
      description: "We couldn't find programs that match all your filters, but here are the closest options you can book right now.",
    }
  }

  return {
    title: "Nothing exact\u2014but we've got great options",
    description: hasExpandableDistanceOptions
      ? "We couldn't find an exact match. Try expanding your radius or adjusting your filters."
      : "We couldn't find an exact match right now. Try adjusting your filters or switching formats to see more options.",
  }
}

export const getCoachSearchProgressContent = (personaId) => {
  const shared = {
    heading: 'Not ready to book? Keep progressing',
    description: "Explore PGA Coach guides tailored to your goals so you're ready when the right program opens up.",
    ctaLabel: 'Explore Helpful Guides',
  }
  if (!personaId) {
    return {
      ...shared,
      title: 'Explore guides for every stage of your golf journey.',
    }
  }
  return {
    ...shared,
    title: PERSONA_PROGRESS_CONTENT[personaId]?.title || 'New to golf? Start with the basics.',
  }
}

export const computeFallbackOfferings = (unfilteredHits = [], filteredIds = new Set(), limit = 6) => {
  if (!unfilteredHits.length) return []
  return unfilteredHits
    .filter((offering) => !filteredIds.has(offering.objectID))
    .slice(0, limit)
}
